import { FormControlLabel, FormGroup, Stack, Switch, Tooltip, Typography, Box } from '@mui/material'
import { CELL_IMAGE_SIZES, CELL_IMAGE_SPACING_OPTIONS } from 'components/cell-visualizations/shared'
import useDisplayImagesControl from 'components/cell-visualizations/useDisplayImagesControl'
import DeepcellLabel from 'components/shared/DeepcellLabel'
import DeepcellPopover from 'components/shared/DeepcellPopover'
import DeepcellPrimaryIconButton from 'components/shared/DeepcellPrimaryIconButton'
import DeepcellSecondarySelect from 'components/shared/DeepcellSecondarySelect'
import ChatIcon from 'components/shared/icons/ChatIcon'
import useFlags from 'components/shared/useFlags'
import { useState } from 'react'
import useEventsManager from 'redux/slices/hooks/useEventsManager'

export const PlotImagesFilter = (): JSX.Element => {
  const { demoEnabled } = useFlags()
  const contactUsVisible = demoEnabled === 'yes'
  const {
    displayImages,
    onDisplayImagesChange,
    imageSize,
    spacingAdjust,
    onImageSizeChange,
    onSpacingAdjustChange,
  } = useDisplayImagesControl()

  const eventsManager = useEventsManager()

  const [showContactUs, setShowContactUs] = useState(false)

  return (
    <>
      <Stack
        id="plot-images-filter"
        direction="row"
        spacing={2}
        data-testid="plot-images-filter"
        alignItems="center"
      >
        <Tooltip title="Display Images (I)" placement="top">
          <FormGroup>
            <FormControlLabel
              control={<Switch checked={displayImages} onChange={onDisplayImagesChange} />}
              sx={{ color: 'black' }}
              label="Display Images (I)"
              labelPlacement="start"
            />
          </FormGroup>
        </Tooltip>

        <div style={{ display: 'flex', gap: '8px' }}>
          <DeepcellLabel label="Image Size" />
          <DeepcellSecondarySelect
            items={CELL_IMAGE_SIZES}
            value={imageSize}
            onChange={onImageSizeChange}
            sx={{ width: '8rem' }}
            formControlProps={{ fullWidth: false }}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <DeepcellLabel label="Image Spacing" />
          <DeepcellSecondarySelect
            items={CELL_IMAGE_SPACING_OPTIONS}
            value={spacingAdjust}
            onChange={onSpacingAdjustChange}
            sx={{ width: '8rem' }}
            formControlProps={{ fullWidth: false }}
          />
        </div>
        {contactUsVisible && (
          <DeepcellPrimaryIconButton
            data-testid="contact-us-button"
            onClick={() => {
              eventsManager.sendContactUsByEvent(new Date().getTime())
              return setShowContactUs(true)
            }}
            size="small"
            outlined
            sx={{
              position: 'absolute',
              bottom: '13px',
              right: '23px',
              paddingY: '10px !important',
              backgroundColor: (t) => t.palette.success.main,
              '&:hover': {
                boxShadow: '0px 0px 10px #5F55D1',
                backgroundColor: (t) => t.palette.success.main,
              },
            }}
            icon={<ChatIcon />}
          />
        )}
      </Stack>

      <DeepcellPopover
        open={showContactUs}
        sx={{
          '& .MuiPopover-paper': {
            width: '450px',
            minHeight: '300px',
            inset: 'unset !important',
            bottom: '13px !important',
            right: '16px !important',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
        onClose={() => setShowContactUs(false)}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary',
              mb: 3,
              fontSize: '1.5rem',
            }}
          >
            Contact Us
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: 'text.secondary',
              lineHeight: 1.6,
              fontSize: '1rem',
            }}
          >
            If you have any inquiries or questions, please reach out to us via email:
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'primary.light',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.main',
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              component="a"
              href="mailto:express@deepcellbio.com"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: '1.2rem',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              express@deepcellbio.com
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            We will get back to you within 24 hours
          </Typography>
        </Box>
      </DeepcellPopover>
    </>
  )
}

export default PlotImagesFilter
